import { Card, Col } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { ScoreGridCategory } from '../../../modules/appraisal/evaluation/ScoreGridCategory.enum'
import { getEmployeeFullName, getReviewerFullName } from '../../../shared/employeeUtil'
import { Employee, Meeting, ScoreGoalDto, ScoreGridDto, EmployeeDisplayShortInfo } from '../../../types'
import PerIcon from '../Icon'

interface TileProp {
  header: string
  scoreGoals: ScoreGoalDto[]
  employee: Employee
  reviewer?: EmployeeDisplayShortInfo | null
}

const Tile: FC<TileProp> = ({ header, scoreGoals, employee, reviewer }) => {
  const [t] = useTranslation()

  return (
    <Card bordered={false} className="fullHeight">
      <PerText appearance="tile-heading" mb="xs">
        {header}
      </PerText>
      <PerFlexRow mb="xxxs" align="middle" justify="center">
        <Col span={15} />
        <Col span={3}>
          <PerFlexRow justify="center">
            <PerIcon
              name="user_fill"
              size={18}
              tooltip={t('appraisalStatusModal.iconTooltipEmployee', { name: getEmployeeFullName(employee) })}
            />
          </PerFlexRow>
        </Col>
        <Col span={3}>
          <PerFlexRow justify="center">
            <PerIcon
              name="user_fill"
              size={18}
              tooltip={reviewer ? t('appraisalStatusModal.iconTooltipReviewer', { name: getReviewerFullName(reviewer) }) : null}
            />
          </PerFlexRow>
        </Col>
        <Col span={3}>
          <PerFlexRow align="middle" justify="center">
            <PerIcon name="meeting_stroke" size={18} tooltip={t('appraisalStatusModal.iconTooltipMeeting')} />
          </PerFlexRow>
        </Col>
      </PerFlexRow>
      {scoreGoals.map((item, idx) => (
        <PerFlexRow key={idx} align="top" justify="center" mb="xxxs">
          <Col span={15}>
            <PerText>{t(`appraisalMeeting.evaluation.grid.headline.${item.viewId}`)}</PerText>
          </Col>
          <Col span={3}>
            <PerText fontWeight="bold" align="center">
              {item.employeeEvaluation || '-'}
            </PerText>
          </Col>
          <Col span={3}>
            <PerText fontWeight="bold" align="center">
              {item.reviewerEvaluation || '-'}
            </PerText>
          </Col>
          <Col span={3}>
            <PerText fontWeight="bold" align="center">
              {item.resultEvaluation || '-'}
            </PerText>
          </Col>
        </PerFlexRow>
      ))}
    </Card>
  )
}

interface MeetingStatusModalEvaluationProps {
  scoreGrid?: ScoreGridDto
  meeting: Meeting
  userIsHR: boolean
}

export const MeetingStatusModalEvaluation: FC<MeetingStatusModalEvaluationProps> = ({ scoreGrid, meeting, userIsHR }) => {
  const [t] = useTranslation()

  if (!scoreGrid) {
    return null
  }

  const tabs = Object.keys(ScoreGridCategory).map((entry) => {
    return { id: entry, title: t(`appraisalMeeting.evaluation.grid.category.${entry}`) }
  })

  const colSpan = 8
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const sharedInformation = meeting.appraisal!.evaluationShared

  const NotShared = () => {
    return (
      <PerFlexRow align="middle" mb="xs">
        <PerIcon name="warning_fill" size={18} className="per-flexRow--fixed" />
        <PerText appearance="quote" ml="xxxs" className="per-flexRow-growShrink">
          {meeting.employee.legalFirstName + ' ' + t('appraisalMeeting.conclusion.evaluation.notShared')}
        </PerText>
      </PerFlexRow>
    )
  }

  return (
    <div style={{ width: '100%' }} data-e2e-evaluation-conclusion data-testid="evaluation-wrapper">
      <>
        {meeting.employeePrepared &&
          !sharedInformation &&
          (!meeting.managerPrepared || !meeting.selfReflectionAndEvaluationVisible) &&
          !userIsHR && <NotShared />}
        <PerFlexRow mb="xs">
          <Col span={colSpan}>
            <Tile
              key={1}
              header={tabs[0].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Customer'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
          <Col span={colSpan}>
            <Tile
              key={1}
              header={tabs[1].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Speed'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
          <Col span={colSpan}>
            <Tile
              key={2}
              header={tabs[2].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Teamwork'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
        </PerFlexRow>

        <PerFlexRow mb="xs">
          <Col span={colSpan}>
            <Tile
              key={3}
              header={tabs[3].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Excellence'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
          <Col span={colSpan}>
            <Tile
              key={4}
              header={tabs[4].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Innovation'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
          <Col span={colSpan}>
            <Tile
              key={5}
              header={tabs[5].title}
              scoreGoals={scoreGrid.scoreGoals.filter((scoreGoal) => scoreGoal.viewId.toString().startsWith('Human'))}
              employee={meeting.employee}
              reviewer={meeting.reviewer}
            />
          </Col>
        </PerFlexRow>
      </>
    </div>
  )
}
